import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Shield, CheckCircle2, XCircle, AlertTriangle, BookOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BOOL_ICON = (v) => v ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />;

export default function InsuranceNegotiation() {
  const [rules, setRules] = useState([]);
  useEffect(() => { api.get("/ai-ops/carrier-rules").then((r) => setRules(r.data)); }, []);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="negotiation-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-purple-600/15 border border-purple-500/20 flex items-center justify-center">
          <Shield className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">AI Intelligence</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Insurance Negotiation Engine</h1>
          <p className="text-sm text-slate-400 mt-1">Carrier behavior patterns · denial likelihood · best arguments per insurer.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {rules.map((r) => (
          <div key={r.name} data-testid={`rule-${r.name.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-medium">{r.name}</h3>
              <Badge className={r.known ? "bg-purple-500/15 text-purple-300 border-purple-500/20" : "bg-slate-800 border-slate-700 text-slate-400"}>
                {r.known ? "PATTERN KNOWN" : "NEW CARRIER"}
              </Badge>
            </div>
            {r.average_response_days && (
              <div className="text-xs text-slate-400 mb-3">Avg response: <span className="text-slate-200 font-mono">{r.average_response_days} days</span></div>
            )}
            {r.known && (
              <div className="space-y-1.5 mb-4">
                <Row icon={r.denies_dual_calibration ? XCircle : CheckCircle2} label="Approves Dual Calibration" value={!r.denies_dual_calibration} />
                <Row icon={r.denies_pre_scan_routinely ? XCircle : CheckCircle2} label="Approves Pre-Scan" value={!r.denies_pre_scan_routinely} />
                <Row icon={r.approves_oem_glass_only_with_proof ? AlertTriangle : CheckCircle2} label="OEM Glass" value={r.approves_oem_glass_only_with_proof ? "Requires proof" : "Liberal"} variant={r.approves_oem_glass_only_with_proof ? "warn" : "good"} />
                {r.requires_oem_documentation && <Row icon={AlertTriangle} label="OEM Docs Required" value="Always" variant="warn" />}
              </div>
            )}
            <div className="rounded-lg border border-purple-500/15 bg-purple-500/5 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-purple-300 mb-1.5 flex items-center gap-1"><BookOpen className="w-3 h-3" />Best Argument</div>
              <div className="text-xs text-slate-300 leading-relaxed">{r.best_argument}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value, variant }) => {
  const color = variant === "warn" ? "text-amber-400" : variant === "good" ? "text-emerald-400" : (value === true || value === "Always") ? "text-emerald-400" : "text-red-400";
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <span className="text-slate-400 flex-1">{label}</span>
      <span className={`${color} font-semibold`}>{typeof value === "boolean" ? (value ? "Yes" : "No") : value}</span>
    </div>
  );
};
